import { BrowserStorageSessionService } from '@propertyfinder/pf-frontend-common/src/service/browser-storage-session/service';
import { useEffect, useState } from 'preact/hooks';

import { useBeforeUnload } from 'common/hooks/before-unload.hook';
import { DataStoreEvent } from 'common/module/data/store.event';

import { PropertyListStatsTealiumImpressionsService } from './impressions.service';

const IMPRESSIONS_QUEUE_STORAGE_KEY = 'ImpressionsQueue';
const MIN_IMPRESSIONS_COUNT_IN_QUEUE = 5;

type PropertiesInQueueType = Map<string, boolean>;

interface ImpressionsSessionStorageInterface {
  data: PropertiesInQueueType;
  date: string;
}

/**
 *
 * {@link https://pfinder.atlassian.net/browse/CX-1111}
 */
export const useListStatsTealiumImpressionsQueue = (): ((id: string) => void) => {
  const [browserStorageSessionService] = useState(BrowserStorageSessionService());
  const [impressionsService] = useState(PropertyListStatsTealiumImpressionsService());
  const [propertiesQueue, setPropertiesQueue] = useState<PropertiesInQueueType>(impressionsService.getState().queue);

  // if the user navigates OR close the tab
  useBeforeUnload(() => {
    const impressionsUnSent = impressionsService.getUnSentImpressions(propertiesQueue);

    if (impressionsUnSent.size > 0) {
      impressionsService.sendImpression(impressionsUnSent);
    }

    impressionsService.destroy();
  });

  useEffect(() => {
    const sync = (): void => {
      setPropertiesQueue(impressionsService.getState().queue);
    };

    impressionsService.getEventEmitter().addListener(DataStoreEvent.updateState, sync);

    return (): void => {
      impressionsService.getEventEmitter().removeListener(DataStoreEvent.updateState, sync);
    };
  }, [impressionsService]);

  useEffect(() => {
    const impressionsUnSent = impressionsService.getUnSentImpressions(propertiesQueue);
    const dataToSave = {
      data: Object.fromEntries(propertiesQueue),
      date: new Date().toISOString(),
    } as unknown as ImpressionsSessionStorageInterface;

    // whatever the case, sets propertiesQueue to the session
    browserStorageSessionService.setData(IMPRESSIONS_QUEUE_STORAGE_KEY, dataToSave);

    if (impressionsUnSent.size === MIN_IMPRESSIONS_COUNT_IN_QUEUE) {
      impressionsService.sendImpression(impressionsUnSent);
    }
  }, [propertiesQueue]);

  return (id): void => {
    if (!propertiesQueue.has(id)) {
      impressionsService.updateState(new Map(propertiesQueue.set(id, false)));
    }
  };
};
